/**
 * Whether the user pointed, typed or scrolled just before the API client asked.
 *
 * The client marks only such requests, and only those keep the browser session alive, so a
 * tab left open still signs out after the idle limit however many polls, heartbeats and
 * stream reconnects it makes. Nothing has to list which requests are timer-driven.
 */

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

/**
 * Input older than this no longer counts. A request a person causes leaves within moments
 * of their input; a poll that a tab resumes after the system slept must not carry input
 * from before it.
 */
export const USER_ACTIVITY_FRESHNESS_MS = 60_000;

let lastInputAt: number | null = null;

function markActive(): void {
  lastInputAt = Date.now();
}

/** Listen for user input on `target`; returns the function that stops listening. */
export function trackUserActivity(target: Window = window): () => void {
  for (const type of ACTIVITY_EVENTS) {
    target.addEventListener(type, markActive, { capture: true, passive: true });
  }
  return () => {
    for (const type of ACTIVITY_EVENTS) {
      target.removeEventListener(type, markActive, { capture: true });
    }
  };
}

/** True once per burst of recent input: the first request after it carries the activity. */
export function consumeUserActivity(): boolean {
  const inputAt = lastInputAt;
  lastInputAt = null;
  return inputAt !== null && Date.now() - inputAt <= USER_ACTIVITY_FRESHNESS_MS;
}
